import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [navSettings, setNavSettings] = useState({ show_standards: true, show_plans: true });
  const location = useLocation();

  // Pages that don't have dark hero sections and need solid header
  const lightBackgroundPages = ['/admin/login', '/admin/projects'];
  const isLightPage = lightBackgroundPages.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    // Check initial scroll position
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch navigation settings
  useEffect(() => {
    const fetchNavSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/site-settings/navigation`);
        if (res.ok) {
          const data = await res.json();
          setNavSettings(data);
        }
      } catch (error) {
        console.error('Error fetching nav settings:', error);
      }
    };
    fetchNavSettings();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsResourcesOpen(false);
    if (isResourcesOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isResourcesOpen]);

  // Use solid header style if scrolled OR if on a light background page
  const useSolidHeader = isScrolled || isLightPage;

  // Build nav links based on visibility settings
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    ...(navSettings.show_plans !== false ? [{ name: 'Plans', path: '/plans' }] : []),
    { name: 'About', path: '/about' },
    ...(navSettings.show_standards !== false ? [{ name: 'Standards', path: '/standards' }] : []),
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'FAQ', path: '/faq' },
  ];

  // Resources menu with categories - Admin removed, portal logins removed
  const resourceCategories = [
    {
      name: 'Customers',
      items: [
        { name: 'Customer Portal Support', url: 'https://www.jobtread.com/resources/help/customer-portal', external: true },
      ]
    },
    {
      name: 'Vendors',
      items: [
        { name: 'Vendor Application', path: '/vendor-application', internal: true },
        { name: 'Vendor Portal Support', url: 'https://www.jobtread.com/resources/help/vendor-portal', external: true },
      ]
    },
    {
      name: 'Careers',
      items: [
        { name: 'Join Our Team', path: '/careers', internal: true },
        { name: 'Employment Application', path: '/employment-application', internal: true },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;

  // Scroll to top when navigating
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useSolidHeader
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-slate-900/80 backdrop-blur-sm'
      }`}
    >
      {/* Top Bar with Login and Project Estimator CTA */}
      <div className={`hidden lg:block border-b ${useSolidHeader ? 'border-slate-200' : 'border-white/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center py-2 gap-6">
            <a
              href={companyInfo.jobtreadLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                useSolidHeader
                  ? 'text-slate-600 hover:text-cyan-600'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <LogIn className="w-3 h-3" />
              Customer / Vendor Login
            </a>
            <Link
              to="/admin/projects"
              className={`inline-flex items-center justify-center px-5 rounded-lg text-sm font-semibold uppercase transition-all h-8 ${
                useSolidHeader
                  ? 'text-slate-700 border border-slate-300 hover:border-slate-400 hover:text-slate-900'
                  : 'text-white border border-white/50 hover:border-white hover:bg-white/10'
              }`}
            >
              Control Center
            </Link>
            <Link to="/estimator">
              <Button
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-5 text-sm uppercase h-8"
              >
                Project Estimator <span className="ml-1 font-bold">$</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4">
            <img
              src={companyInfo.logoIcon}
              alt="C3 Contracting"
              className="h-20 w-auto"
            />
            <div className="flex flex-col justify-center">
              <span 
                className={`font-extrabold uppercase leading-none whitespace-nowrap ${useSolidHeader ? 'text-cyan-600' : 'text-white'}`}
                style={{ fontSize: '26px', letterSpacing: '0.02em' }}
              >
                C3 Contracting
              </span>
              <span 
                className={`font-semibold uppercase leading-tight whitespace-nowrap ${useSolidHeader ? 'text-cyan-600' : 'text-white'}`}
                style={{ fontSize: '10.5px', letterSpacing: '0.19em', marginTop: '2px' }}
              >
                Building with a Deeper Purpose
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <nav className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-[15px] font-medium uppercase transition-colors duration-200 relative group ${
                    useSolidHeader
                      ? isActive(link.path)
                        ? 'text-cyan-600'
                        : 'text-slate-700 hover:text-cyan-600'
                      : isActive(link.path)
                      ? 'text-cyan-400'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-cyan-500 transition-all duration-200 ${
                      isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </Link>
              ))}
              
              {/* Resources Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsResourcesOpen(!isResourcesOpen);
                  }}
                  className={`text-[15px] font-medium uppercase transition-colors duration-200 flex items-center gap-1 ${
                    useSolidHeader
                      ? 'text-slate-700 hover:text-cyan-600'
                      : 'text-white hover:text-cyan-400'
                  }`}
                >
                  Resources
                  <ChevronDown className={`w-4 h-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isResourcesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
                    {resourceCategories.map((category, idx) => (
                      <div key={category.name}>
                        {idx > 0 && <div className="border-t border-slate-100 my-2" />}
                        <div className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          {category.name}
                        </div>
                        {category.items.map((item) => (
                          item.internal ? (
                            <Link
                              key={item.name}
                              to={item.path}
                              className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-cyan-600 transition-colors"
                              onClick={() => setIsResourcesOpen(false)}
                            >
                              {item.name}
                            </Link>
                          ) : (
                            <a
                              key={item.name}
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-cyan-600 transition-colors"
                            >
                              {item.name}
                            </a>
                          )
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Link */}
              <Link
                to="/contact"
                className={`text-[15px] font-medium uppercase transition-colors duration-200 relative group ${
                  useSolidHeader
                    ? isActive('/contact')
                      ? 'text-cyan-600'
                      : 'text-slate-700 hover:text-cyan-600'
                    : isActive('/contact')
                    ? 'text-cyan-400'
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                Contact
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-cyan-500 transition-all duration-200 ${
                    isActive('/contact') ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            </nav>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${useSolidHeader ? 'text-slate-800' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${useSolidHeader ? 'text-slate-800' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-8 bg-slate-900/95 backdrop-blur-md rounded-lg max-h-[calc(100vh-120px)] overflow-y-auto overscroll-contain touch-pan-y">
            <nav className="flex flex-col gap-1 pt-4 px-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-4 py-4 rounded-lg text-base font-medium transition-colors active:bg-white/20 ${
                    isActive(link.path)
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-white hover:bg-white/10'
                  }`}
                  style={{ minHeight: '48px', touchAction: 'manipulation' }}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Contact Link - moved up for better visibility */}
              <Link
                to="/contact"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(false);
                }}
                className={`px-4 py-4 rounded-lg text-base font-medium transition-colors active:bg-white/20 ${
                  isActive('/contact')
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-white hover:bg-white/10'
                }`}
                style={{ minHeight: '48px', touchAction: 'manipulation' }}
              >
                Contact
              </Link>
              
              {/* Customer / Vendor Login - above Control Center */}
              <a
                href={companyInfo.jobtreadLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(false);
                }}
                className="mx-2 px-4 py-3 rounded-lg text-base font-semibold text-white border border-white/50 text-center mt-2"
                style={{ minHeight: '48px', touchAction: 'manipulation' }}
              >
                Customer / Vendor Login
              </a>
              
              {/* Control Center - between Customer Login and Project Estimator */}
              <Link
                to="/admin/projects"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(false);
                }}
                className="mx-2 px-4 py-3 rounded-lg text-base font-semibold text-white border border-white/50 text-center mt-2"
                style={{ minHeight: '48px', touchAction: 'manipulation' }}
              >
                Control Center
              </Link>
              
              {/* Project Estimator CTA - prominent placement */}
              <Link
                to="/estimator"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(false);
                }}
                className="px-2 mt-2"
                style={{ touchAction: 'manipulation' }}
              >
                <Button className="w-full bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800 text-white font-semibold py-4 text-base" style={{ minHeight: '48px' }}>
                  Project Estimator <span className="ml-1 font-bold">$</span>
                </Button>
              </Link>
              
              {/* Resources Section in Mobile - Categorized */}
              <div className="px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mt-4 border-t border-white/10 pt-4">
                Resources
              </div>
              
              {resourceCategories.map((category) => (
                <div key={category.name} className="mb-2">
                  <div className="px-4 py-2 text-xs text-cyan-400/70 uppercase tracking-wider">
                    {category.name}
                  </div>
                  {category.items.map((item) => (
                    item.internal ? (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                        }}
                        className="px-6 py-4 rounded-lg text-base font-medium transition-colors text-white hover:bg-white/10 active:bg-white/20 block"
                        style={{ minHeight: '48px', touchAction: 'manipulation' }}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <a
                        key={item.name}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMobileMenuOpen(false);
                        }}
                        className="px-6 py-4 rounded-lg text-base font-medium transition-colors text-white hover:bg-white/10 active:bg-white/20 block"
                        style={{ minHeight: '48px', touchAction: 'manipulation' }}
                      >
                        {item.name}
                      </a>
                    )
                  ))}
                </div>
              ))}
              
              {/* Extra padding at bottom to avoid Safari's gesture area */}
              <div className="h-16" aria-hidden="true"></div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
